import { User } from "@/types/user";
import { api } from "./api";
import { storage } from "./storage";
import type { AuthResponse } from "@/types";

export const authService = {
  /** Exchange a Google OAuth authorization code for a Closai auth token. */
  async signInWithGoogle(code: string): Promise<{ token: string; user: User }> {
    const redirectUri = encodeURIComponent(import.meta.env.VITE_GOOGLE_OAUTH_REDIRECT_URI);
    const { data } = await api.get<AuthResponse>(
      `/oauth/google/login/?code=${code}&redirect_uri=${redirectUri}&source=web`
    );
    storage.setToken(data.token);
    return data;
  },

  async signInWithApple(code: string): Promise<{ token: string; user: User }> {
    const redirectUri = encodeURIComponent(import.meta.env.VITE_APPLE_REDIRECT_URI);
    const { data } = await api.get<AuthResponse>(
      `/oauth/apple/login/?code=${code}&redirect_uri=${redirectUri}&source=web`
    );
    storage.setToken(data.token);
    return data;
  },

  /** Sign in with email and password. */
  async signIn(username: string, password: string): Promise<User & { token: string }> {
    const { data } = await api.post<User & { token: string }>("/login/", { username, password });
    storage.setToken(data.token);
    return data;
  },

  /** Create a new account. */
  async signUp(params: {
    email: string;
    first_name: string;
    last_name: string;
    password: string;
  }): Promise<{ token: string; user: User }> {
    const { data } = await api.post<AuthResponse>("/signup/", {
      ...params,
      is_staff: false
    });
    storage.setToken(data.token);
    return data;
  },

  /** Fetch the currently authenticated user. */
  async getMe(): Promise<User> {
    const { data } = await api.get<User>("/account/me/");
    return data;
  },

  /** Update the current user's profile. */
  async updateMe(userData: Partial<User>): Promise<User> {
    const { data } = await api.patch<User>("/account/me/", userData);
    return data;
  },

  async getInventory(inventoryId: string): Promise<any> {
    const { data } = await api.get<any>(`/account/inventory/${inventoryId}/`);
    return data;
  },

  /** Sign out — clear stored credentials. */
  signOut(): void {
    storage.clear();
  }
};
